import {
  Button,
  Center,
  Divider,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { memo, useCallback, useMemo, useState } from "react";
import {
  DeployEnvironment,
  DEPLOY_ENVIRONMENT_HOSTS,
  DEPLOY_ENVIRONMENT_LABELS,
  DeployResult,
  PRODUCTION_ENVIRONMENTS,
} from "../../api/quests/quests";
import useDeployQuest from "../../hooks/quests/useDeployQuest";
import useQuestOrNull from "../../hooks/quests/useQuestOrNull";
import ConfirmModal from "../modals/ConfirmModal";

interface QuestDeployButtonProps {
  questId: string;
}

const ALL_ENVIRONMENTS = Object.keys(DEPLOY_ENVIRONMENT_HOSTS) as DeployEnvironment[];

function QuestDeployButton({ questId }: QuestDeployButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isResultsOpen, onOpen: onResultsOpen, onClose: onResultsClose } = useDisclosure();

  const { quest } = useQuestOrNull(questId);
  const questVersion = quest?.deployVersion ?? "?.?.?";
  const questName = quest?.name ?? "UNKNOWN";

  const [error, setError] = useState<string>("");
  const [results, setResults] = useState<DeployResult[]>([]);

  const { isDeploying, deployQuest, isDeployingToProduction, deployToProduction } = useDeployQuest();

  // The environment this editor instance is served from — offered as a deploy target
  // for every OTHER environment. localhost is treated as dev.
  const hostname = window.location.hostname;
  const currentEnvironment = useMemo<DeployEnvironment | undefined>(() => {
    if (hostname === "localhost") {
      return "dev";
    }

    return ALL_ENVIRONMENTS.find((environment) => DEPLOY_ENVIRONMENT_HOSTS[environment] === hostname);
  }, [hostname]);

  const handleDeployQuest = useCallback(
    (questId: string, environment: DeployEnvironment) => {
      deployQuest(
        { questId, environment },
        {
          onError: (error) => {
            onOpen();

            setError(error.message);
          },
        }
      );
    },
    [deployQuest, onOpen]
  );

  const handleDeployToProduction = useCallback(
    (questId: string) => {
      deployToProduction({ questId })
        .then((deployResults) => {
          setResults(deployResults);
          onResultsOpen();
        })
        // errors are surfaced via the hook's toast; nothing to do here
        .catch(() => undefined);
    },
    [deployToProduction, onResultsOpen]
  );

  return (
    <>
      <Popover placement={"left-end"}>
        <PopoverTrigger>
          <Button color={"white"} isLoading={isDeploying || isDeployingToProduction}>
            <Text color={"white"} textTransform={"uppercase"}>
              Deploy To
            </Text>
          </Button>
        </PopoverTrigger>

        <Portal>
          <PopoverContent mx={2} bg={"theme.dark.background"} borderColor={"green.600"} borderWidth={2}>
            <PopoverHeader>
              <Text color={"white"} casing={"uppercase"}>
                Deploy to
              </Text>
            </PopoverHeader>

            <PopoverBody>
              <Stack>
                {ALL_ENVIRONMENTS.filter((environment) => environment !== currentEnvironment).map((environment) => (
                  <ConfirmModal
                    key={environment}
                    color={"white"}
                    onConfirm={() => handleDeployQuest(questId, environment)}
                    title={DEPLOY_ENVIRONMENT_LABELS[environment]}
                  >
                    <Text color={"white"} casing={"uppercase"}>
                      Are you sure you want to deploy to {DEPLOY_ENVIRONMENT_LABELS[environment]}?
                    </Text>
                  </ConfirmModal>
                ))}

                <Divider borderColor={"green.600"} />

                <ConfirmModal
                  color={"white"}
                  onConfirm={() => handleDeployToProduction(questId)}
                  title={"ALL PRODUCTION"}
                >
                  <Stack>
                    <Text color={"white"} casing={"uppercase"}>
                      Deploy {questName} v{questVersion} to ALL production regions?
                    </Text>
                    <Text color={"white"}>
                      {PRODUCTION_ENVIRONMENTS.map((environment) => DEPLOY_ENVIRONMENT_LABELS[environment]).join(", ")}
                    </Text>
                    <Text color={"orange.300"} casing={"uppercase"}>
                      This writes to every live production region and clears in-progress player progress on a version
                      change.
                    </Text>
                  </Stack>
                </ConfirmModal>
              </Stack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>

      <Modal isOpen={isOpen} onClose={onClose} size={"full"}>
        <ModalOverlay />
        <ModalContent bg={"theme.dark.background"} borderColor={"red.800"} borderRadius={0} borderWidth={2}>
          <ModalHeader>
            <Text color={"red.800"} casing={"uppercase"}>
              {error}
            </Text>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody display={"flex"}>
            <Center p={4} flexGrow={1}>
              <Text color={"red.800"} casing={"uppercase"}>
                Quest Deploy Failed
              </Text>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isResultsOpen} onClose={onResultsClose}>
        <ModalOverlay />
        <ModalContent bg={"theme.dark.background"} borderColor={"green.600"} borderWidth={2}>
          <ModalHeader>
            <Text color={"white"} casing={"uppercase"}>
              Production deploy results
            </Text>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <List spacing={2}>
              {results.map(({ environment, ok, error }) => (
                <ListItem key={environment}>
                  <Text color={ok ? "green.400" : "red.400"}>
                    {ok ? "✅" : "❌"} {DEPLOY_ENVIRONMENT_LABELS[environment]}
                    {!ok && error ? `: ${error}` : ""}
                  </Text>
                </ListItem>
              ))}
            </List>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default memo(QuestDeployButton);
